// src/pages/PrivacyPolicyPage.jsx
import React from 'react';
import useLegalLanguage from '../hooks/useLegalLanguage';
import styled from 'styled-components';
import {
    PageTitle,
    LanguageToggle,
    LangButton,
    ContactBlock,
} from '../components/ui/PageElements';
import PageMeta from '../components/PageMeta';

const PolicyWrapper = styled.div`
    max-width: 800px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    line-height: 1.7;

    h2 {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-top: 40px;
        margin-bottom: 20px;
        color: var(--primary-color);
        position: relative;

        &:before {
            content: "§";
            color: var(--secondary-color);
            margin-right: 10px;
            opacity: 0.7;
        }
    }

    h3 {
        font-size: 18px;
        font-weight: 700;
        margin-top: 30px;
        margin-bottom: 15px;
        color: var(--text-color);
        opacity: 0.9;
    }

    p, li {
        font-size: 16px;
        color: #cccccc;
        margin-bottom: 15px;
    }

    ul {
        list-style: disc;
        padding-left: 30px;
        margin-bottom: 20px;
    }

    a {
        color: var(--secondary-color);
        text-decoration: underline;
        text-underline-offset: 3px;

        &:hover {
            color: var(--primary-color);
        }
    }

    strong {
        color: var(--text-color);
        font-weight: 700;
    }
`;

const LastUpdatedText = styled.p`
    font-style: italic;
    color: var(--text-muted);
    margin-bottom: 30px !important;
    font-size: 14px !important;
`;

const CURRENT_DATE = new Date().toLocaleDateString('en-CA');

const content = {
    en: {
        title: "Privacy Policy",
        lastUpdated: "Last Updated",

        // Section 0
        section0Title: "0. Our Approach to Your Privacy",
        section0Content: `This Privacy Policy outlines how I, Lucas Perron (also "LPERRON_DEV"), handle
            information when you visit my portfolio website. My approach is simple:
            I value your privacy, and this site is intentionally designed to collect minimal data.
            I haven't implemented extensive tracking because, frankly, I'm focused on
            showcasing my work, not analyzing your browsing habits.`,

        // Section 1
        section1Title: "1. Information Handling: What We Collect and Why",
        section1_1Title: "1.1. Information I Do Not Collect (By Design)",
        section1_1Intro: "To be clear, this website does not intentionally collect, store, or process the following types of data from visitors:",
        section1_1Items: [
            "Personal identification details like your name, email address, or phone number (except if you voluntarily provide them via the contact form, as detailed below).",
            "User accounts, login credentials, or passwords (this site does not have user registration).",
            "Detailed tracking or analytics data about your specific browsing behavior across this site or others."
        ],
        section1_1Outro: "The reason is straightforward: I don't need this information for the site to function or to display my portfolio, so I don't ask for it or try to gather it.",

        section1_2Title: "1.2. Information Automatically Logged by Web Hosting",
        section1_2Intro: "My web hosting provider, Netlify, automatically collects and stores certain information as part of their standard service operation. This is typical for most web hosting and helps them maintain their infrastructure. This may include:",
        section1_2Items: [
            "Your IP address (Netlify's policy is to typically delete these server logs after 30 days).",
            "The type of browser and operating system you use.",
            "The date and time of your visit.",
            "The pages you accessed on this site."
        ],
        section1_2Outro: "I do not use this server-level information for individual tracking or user profiling.",

        section1_3Title: "1.3. Information You Provide When Contacting Me",
        section1_3Intro: "If you choose to use the contact form on this website:",
        section1_3Items: [
            "The details you provide (your name, email address, and message content) are processed by a service called Web3Forms.",
            "This information is then forwarded directly to my email address.",
            "I will use these details solely to understand and respond to your inquiry.",
            "I do not add your contact information to marketing lists, nor do I share it with other third parties for marketing purposes.",
            "I retain these communications only for the duration necessary to address your query or for our ongoing conversation if applicable."
        ],

        section1_4Title: "1.4. Information Collected During Purchases",
        section1_4Intro: "If you purchase RSO, the transaction is handled by either Paddle (our Merchant of Record) when purchased via lperron.com, or Gumroad when purchased via their platform. Both platforms collect:",
        section1_4Items: [
            "Your name and email address",
            "Your country of residence (for tax purposes)",
            "Payment information (processed securely by their payment providers)"
        ],
        section1_4Outro: "I do not have direct access to your payment details. For Paddle purchases, I receive the information necessary to generate your license key. For Gumroad purchases, Gumroad generates and delivers your license key directly.",

        section1_5Title: "1.5. License and Activation Data",
        section1_5Intro: "When you activate your license in the software, the following information is stored to manage your license:",
        section1_5Items: [
            "Your license key (generated by Paddle or Gumroad)",
            "Your email address (when available from the purchase)",
            "A hardware identifier generated by your computer (used to track activations)",
            "Activation timestamps"
        ],
        section1_5Outro: "This data is stored securely on our servers (Netlify) and is used solely to verify your license and manage activation limits. Hardware identifiers are anonymized hashes and cannot be used to identify your device beyond license verification.",

        // Section 2
        section2Title: "2. Cookies: Technical & Necessary Only",
        section2Intro: "This website uses a small number of \"technical cookies.\" These are essential for the website to function correctly.",
        section2Items: [
            "These cookies do not track your activity across different websites.",
            "They are generally session-based and are deleted when you close your browser.",
            "Because these are strictly necessary for basic site operation and not for tracking, I don't display a separate cookie consent banner for them."
        ],

        // Section 3
        section3Title: "3. Third-Party Services Utilized",
        section3Intro: "To provide certain features and ensure the site runs effectively, this website relies on the following third-party services. Each service has its own privacy policy, which I recommend reviewing if you have specific concerns:",
        section3Services: [
            { name: "Netlify", desc: "Hosts the website and delivers its content to you. Also stores license and activation data securely.", url: "https://www.netlify.com/privacy/" },
            { name: "Paddle", desc: "Acts as our Merchant of Record for product sales. Handles payment processing, tax compliance, and order fulfillment.", url: "https://www.paddle.com/legal/privacy" },
            { name: "Resend", desc: "Sends transactional emails, including license key delivery after purchase.", url: "https://resend.com/legal/privacy-policy" },
            { name: "Let's Encrypt", desc: "Manages the SSL certificate that secures the connection to this site (handled through Netlify).", url: "https://letsencrypt.org/privacy/" },
            { name: "GitLab", desc: "Hosts some of the images and assets displayed on this website.", url: "https://about.gitlab.com/privacy/" },
            { name: "Web3Forms", desc: "Processes submissions from the contact form, handling the information you provide (name, email, message) to ensure it reaches my email.", url: "https://web3forms.com/privacy" }
        ],
        section3Outro: "Apart from these integrations, the website's core components run within your browser and do not transmit personal data to other external services.",

        // Section 4
        section4Title: "4. Your Data Rights",
        section4Intro: "I believe in providing clear rights regarding your personal data to all users, reflecting the principles of regulations like the GDPR. Regardless of your location, if I hold any personal data about you (primarily from contact form submissions), you have the following rights:",
        section4Rights: [
            { name: "The Right to Access", desc: "You can ask for details about any personal data I hold about you." },
            { name: "The Right to Rectification", desc: "You can ask to correct inaccurate data." },
            { name: "The Right to Erasure (Right to be Forgotten)", desc: "You can request the deletion of your personal data." },
            { name: "The Right to Restrict Processing", desc: "You can ask me to limit the use of your personal data." },
            { name: "The Right to Data Portability", desc: "You can request your data in a commonly used, machine-readable format." }
        ],
        section4Outro: "If you have contacted me via the form and wish to exercise any of these rights, please use the contact information provided in Section 7. I do not sell or share your personal information with third parties for their direct marketing purposes, nor for cross-context behavioral advertising.",

        // Section 5
        section5Title: "5. Children's Privacy",
        section5Content: "This website is not intended for individuals under the age of 13. I do not knowingly collect personal information from children under 13. If I become aware that I have inadvertently received personal information from a child under 13, I will delete such information from my records.",

        // Section 6
        section6Title: "6. Changes to This Privacy Policy",
        section6Content: "I may update this Privacy Policy periodically to reflect changes in my practices or for other operational, legal, or regulatory reasons. The \"Last Updated\" date at the top of this policy will indicate when revisions were made. I encourage you to review this policy occasionally.",

        // Section 7
        section7Title: "7. Contact Information",
        section7Intro: "If you have any questions or concerns about this Privacy Policy or my data handling practices, please contact me:",

        // Footer
        footerText: "This Privacy Policy is intended to be clear and uphold strong privacy standards for all users, aligning with the principles of data protection regulations like the EU GDPR."
    },

    fr: {
        title: "Politique de Confidentialité",
        lastUpdated: "Dernière mise à jour",

        // Section 0
        section0Title: "0. Notre approche de votre vie privée",
        section0Content: `Cette Politique de Confidentialité décrit comment je, Lucas Perron (aussi "LPERRON_DEV"),
            gère les informations lorsque vous visitez mon site portfolio. Mon approche est simple :
            je respecte votre vie privée, et ce site est intentionnellement conçu pour collecter un minimum de données.
            Je n'ai pas mis en place de tracking extensif car, franchement, je me concentre sur
            la présentation de mon travail, pas sur l'analyse de vos habitudes de navigation.`,

        // Section 1
        section1Title: "1. Gestion des informations : ce que nous collectons et pourquoi",
        section1_1Title: "1.1. Informations que je ne collecte pas (par conception)",
        section1_1Intro: "Pour être clair, ce site ne collecte, stocke ou traite pas intentionnellement les types de données suivants :",
        section1_1Items: [
            "Les informations d'identification personnelle comme votre nom, adresse email ou numéro de téléphone (sauf si vous les fournissez volontairement via le formulaire de contact, comme détaillé ci-dessous).",
            "Les comptes utilisateurs, identifiants de connexion ou mots de passe (ce site n'a pas d'inscription utilisateur).",
            "Les données de tracking ou d'analytics détaillées sur votre comportement de navigation sur ce site ou d'autres."
        ],
        section1_1Outro: "La raison est simple : je n'ai pas besoin de ces informations pour que le site fonctionne ou pour afficher mon portfolio, donc je ne les demande pas et n'essaie pas de les collecter.",

        section1_2Title: "1.2. Informations automatiquement enregistrées par l'hébergeur",
        section1_2Intro: "Mon hébergeur, Netlify, collecte et stocke automatiquement certaines informations dans le cadre de son fonctionnement standard. C'est typique pour la plupart des hébergeurs web et les aide à maintenir leur infrastructure. Cela peut inclure :",
        section1_2Items: [
            "Votre adresse IP (la politique de Netlify est généralement de supprimer ces logs serveur après 30 jours).",
            "Le type de navigateur et système d'exploitation que vous utilisez.",
            "La date et l'heure de votre visite.",
            "Les pages que vous avez consultées sur ce site."
        ],
        section1_2Outro: "Je n'utilise pas ces informations de niveau serveur pour du tracking individuel ou du profilage utilisateur.",

        section1_3Title: "1.3. Informations que vous fournissez en me contactant",
        section1_3Intro: "Si vous choisissez d'utiliser le formulaire de contact sur ce site :",
        section1_3Items: [
            "Les informations que vous fournissez (votre nom, adresse email et contenu du message) sont traitées par un service appelé Web3Forms.",
            "Ces informations sont ensuite transmises directement à mon adresse email.",
            "J'utiliserai ces informations uniquement pour comprendre et répondre à votre demande.",
            "Je n'ajoute pas vos coordonnées à des listes marketing, et je ne les partage pas avec des tiers à des fins marketing.",
            "Je conserve ces communications uniquement le temps nécessaire pour traiter votre demande ou pour notre conversation en cours si applicable."
        ],

        section1_4Title: "1.4. Informations collectées lors des achats",
        section1_4Intro: "Si vous achetez RSO, la transaction est gérée soit par Paddle (notre Merchant of Record) lors d'un achat via lperron.com, soit par Gumroad lors d'un achat via leur plateforme. Les deux plateformes collectent :",
        section1_4Items: [
            "Votre nom et adresse email",
            "Votre pays de résidence (pour les taxes)",
            "Les informations de paiement (traitées de manière sécurisée par leurs prestataires de paiement)"
        ],
        section1_4Outro: "Je n'ai pas accès direct à vos informations de paiement. Pour les achats Paddle, je reçois les informations nécessaires pour générer votre clé de licence. Pour les achats Gumroad, Gumroad génère et livre votre clé de licence directement.",

        section1_5Title: "1.5. Données de licence et d'activation",
        section1_5Intro: "Lorsque vous activez votre licence dans le logiciel, les informations suivantes sont stockées pour gérer votre licence :",
        section1_5Items: [
            "Votre clé de licence (générée par Paddle ou Gumroad)",
            "Votre adresse email (lorsque disponible depuis l'achat)",
            "Un identifiant matériel généré par votre ordinateur (utilisé pour suivre les activations)",
            "Les horodatages d'activation"
        ],
        section1_5Outro: "Ces données sont stockées de manière sécurisée sur nos serveurs (Netlify) et sont utilisées uniquement pour vérifier votre licence et gérer les limites d'activation. Les identifiants matériels sont des empreintes anonymisées et ne peuvent pas être utilisés pour identifier votre appareil au-delà de la vérification de licence.",

        // Section 2
        section2Title: "2. Cookies : techniques et nécessaires uniquement",
        section2Intro: "Ce site utilise un petit nombre de \"cookies techniques\". Ils sont essentiels au bon fonctionnement du site.",
        section2Items: [
            "Ces cookies ne suivent pas votre activité sur différents sites web.",
            "Ils sont généralement basés sur la session et sont supprimés lorsque vous fermez votre navigateur.",
            "Comme ils sont strictement nécessaires au fonctionnement de base du site et non pour le tracking, je n'affiche pas de bannière de consentement aux cookies séparée."
        ],

        // Section 3
        section3Title: "3. Services tiers utilisés",
        section3Intro: "Pour fournir certaines fonctionnalités et assurer le bon fonctionnement du site, ce site s'appuie sur les services tiers suivants. Chaque service a sa propre politique de confidentialité, que je vous recommande de consulter si vous avez des préoccupations spécifiques :",
        section3Services: [
            { name: "Netlify", desc: "Héberge le site web et vous délivre son contenu. Stocke également les données de licence et d'activation de manière sécurisée.", url: "https://www.netlify.com/privacy/" },
            { name: "Paddle", desc: "Agit comme notre Merchant of Record pour les ventes de produits. Gère le traitement des paiements, la conformité fiscale et l'exécution des commandes.", url: "https://www.paddle.com/legal/privacy" },
            { name: "Resend", desc: "Envoie les emails transactionnels, notamment la livraison des clés de licence après achat.", url: "https://resend.com/legal/privacy-policy" },
            { name: "Let's Encrypt", desc: "Gère le certificat SSL qui sécurise la connexion à ce site (géré via Netlify).", url: "https://letsencrypt.org/privacy/" },
            { name: "GitLab", desc: "Héberge certaines images et ressources affichées sur ce site.", url: "https://about.gitlab.com/privacy/" },
            { name: "Web3Forms", desc: "Traite les soumissions du formulaire de contact, gérant les informations que vous fournissez (nom, email, message) pour qu'elles me parviennent.", url: "https://web3forms.com/privacy" }
        ],
        section3Outro: "En dehors de ces intégrations, les composants principaux du site fonctionnent dans votre navigateur et ne transmettent pas de données personnelles à d'autres services externes.",

        // Section 4
        section4Title: "4. Vos droits sur vos données",
        section4Intro: "Je tiens à vous garantir des droits clairs concernant vos données personnelles, conformément aux principes du RGPD. Quel que soit votre lieu de résidence, si je détiens des données personnelles vous concernant (principalement via le formulaire de contact), vous avez les droits suivants :",
        section4Rights: [
            { name: "Droit d'accès", desc: "Vous pouvez demander des détails sur les données personnelles que je détiens vous concernant." },
            { name: "Droit de rectification", desc: "Vous pouvez demander la correction de données inexactes." },
            { name: "Droit à l'effacement (droit à l'oubli)", desc: "Vous pouvez demander la suppression de vos données personnelles." },
            { name: "Droit à la limitation du traitement", desc: "Vous pouvez me demander de limiter l'utilisation de vos données personnelles." },
            { name: "Droit à la portabilité", desc: "Vous pouvez demander vos données dans un format couramment utilisé et lisible par machine." }
        ],
        section4Outro: "Si vous m'avez contacté via le formulaire et souhaitez exercer l'un de ces droits, veuillez utiliser les coordonnées fournies dans la Section 7. Je ne vends ni ne partage vos informations personnelles avec des tiers à des fins de marketing direct ou de publicité comportementale.",

        // Section 5
        section5Title: "5. Confidentialité des enfants",
        section5Content: "Ce site n'est pas destiné aux personnes de moins de 13 ans. Je ne collecte pas sciemment d'informations personnelles auprès d'enfants de moins de 13 ans. Si je prends connaissance que j'ai reçu par inadvertance des informations personnelles d'un enfant de moins de 13 ans, je supprimerai ces informations de mes dossiers.",

        // Section 6
        section6Title: "6. Modifications de cette politique",
        section6Content: "Je peux mettre à jour cette Politique de Confidentialité périodiquement pour refléter des changements dans mes pratiques ou pour d'autres raisons opérationnelles, légales ou réglementaires. La date \"Dernière mise à jour\" en haut de cette politique indiquera quand des révisions ont été effectuées. Je vous encourage à consulter cette politique occasionnellement.",

        // Section 7
        section7Title: "7. Coordonnées",
        section7Intro: "Si vous avez des questions ou préoccupations concernant cette Politique de Confidentialité ou mes pratiques de gestion des données, veuillez me contacter :",

        // Footer
        footerText: "Cette Politique de Confidentialité vise à être claire et à maintenir des standards élevés de protection de la vie privée pour tous les utilisateurs, conformément aux principes des réglementations de protection des données comme le RGPD européen."
    }
};

const PrivacyPolicyPage = () => {
    const [lang, setLang] = useLegalLanguage();
    const t = content[lang];

    return (
        <PolicyWrapper>
            <PageMeta
                title="Privacy Policy"
                description="Privacy Policy for lperron.com. Learn how we collect, use, and protect your personal information."
                canonicalPath="/privacy-policy"
            />
            <LanguageToggle>
                <LangButton $active={lang === 'en'} onClick={() => setLang('en')}>EN</LangButton>
                <LangButton $active={lang === 'fr'} onClick={() => setLang('fr')}>FR</LangButton>
            </LanguageToggle>
            <PageTitle style={{ textAlign: 'left', fontSize: '36px', marginBottom: '10px' }}>{t.title}</PageTitle>
            <LastUpdatedText>{t.lastUpdated}: {CURRENT_DATE}</LastUpdatedText>

            {/* Section 0 */}
            <h2>{t.section0Title}</h2>
            <p>{t.section0Content}</p>

            {/* Section 1 */}
            <h2>{t.section1Title}</h2>

            <h3>{t.section1_1Title}</h3>
            <p>{t.section1_1Intro}</p>
            <ul>
                {t.section1_1Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p>{t.section1_1Outro}</p>

            <h3>{t.section1_2Title}</h3>
            <p>{t.section1_2Intro}</p>
            <ul>
                {t.section1_2Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p>{t.section1_2Outro}</p>

            <h3>{t.section1_3Title}</h3>
            <p>{t.section1_3Intro}</p>
            <ul>
                {t.section1_3Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            <h3>{t.section1_4Title}</h3>
            <p>{t.section1_4Intro}</p>
            <ul>
                {t.section1_4Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p>{t.section1_4Outro}</p>

            <h3>{t.section1_5Title}</h3>
            <p>{t.section1_5Intro}</p>
            <ul>
                {t.section1_5Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p>{t.section1_5Outro}</p>

            {/* Section 2 */}
            <h2>{t.section2Title}</h2>
            <p>{t.section2Intro}</p>
            <ul>
                {t.section2Items.map((item, i) => <li key={i}>{item}</li>)}
            </ul>

            {/* Section 3 */}
            <h2>{t.section3Title}</h2>
            <p>{t.section3Intro}</p>
            <ul>
                {t.section3Services.map((service, i) => (
                    <li key={i}>
                        <strong>{service.name}:</strong> {service.desc}
                        <br />
                        <a href={service.url} target="_blank" rel="noopener noreferrer">
                            {service.name} {lang === 'en' ? 'Privacy Policy' : 'Politique de Confidentialité'}
                        </a>
                    </li>
                ))}
            </ul>
            <p>{t.section3Outro}</p>

            {/* Section 4 */}
            <h2>{t.section4Title}</h2>
            <p>{t.section4Intro}</p>
            <ul>
                {t.section4Rights.map((right, i) => (
                    <li key={i}><strong>{right.name}:</strong> {right.desc}</li>
                ))}
            </ul>
            <p>{t.section4Outro}</p>

            {/* Section 5 */}
            <h2>{t.section5Title}</h2>
            <p>{t.section5Content}</p>

            {/* Section 6 */}
            <h2>{t.section6Title}</h2>
            <p>{t.section6Content}</p>

            {/* Section 7 */}
            <h2>{t.section7Title}</h2>
            <p>{t.section7Intro}</p>
            <ContactBlock lang={lang} />

            <hr style={{ margin: '40px 0', borderColor: 'var(--grid-color)' }} />

            <p style={{ fontSize: '14px', color: 'var(--text-muted)', textAlign: 'center' }}>
                {t.footerText}
            </p>

        </PolicyWrapper>
    );
};

export default PrivacyPolicyPage;
